// Decline this interview (D17, CR-083 "Interviewers accept/decline; declines notify HM, Recruiter and Coordinator and
// trigger panel change."): the round and its interview date, a required reason, the sentence that a decline opens the
// panel-change workflow and — only when the interview is inside the published notice window — V1's acknowledgement that
// the interviewer told their manager. Whether the window is reached is the server's reading, never the browser's clock:
// the caller passes `withinWindow` from what it read, and a refusal naming `notified_manager` (the window was reached
// after the read) shows the acknowledgement from then on, as V1's RescheduleReplaceDialog does. The decline itself, the
// notices and the Blocked panel change it opens are `panel.decline`'s, in one transaction.
//
// `DeclineForm` is the body from props (no reads), for the SSR suite; `DeclineDialog` adds the state and the frame.

import { useEffect, useId, useState, type ReactNode } from "react";
import {
  CheckTick, Dialog, ErrorStrip, Field, KeyValue, TextArea,
  ACTION_LABELS, LABEL_TEMPLATES, COMMON_LABELS, DIALOG_LABELS, SLIDE_LABELS, formatDateTime, type Instant,
} from "@/mc";
import { AppError } from "@/api/runtime";
import { errorText } from "@/api/useTool";
import { ACKNOWLEDGEMENT, notifyWithin } from "@/views/packets/RescheduleReplaceDialog";
import { ACK_LABELS, CHANGE_LABELS } from "./labels";
import "./change.css";

/** His name for a round (glossary §22): V1's stored "Onsite Round n" reads as Final Interview Loop · Round n, as the
 *  server's `shell_vocab.his_title` rewrites it; any other label is drawn as given. */
const hisRoundLabel = (label: string): string =>
  label.replace(/(?<![\w])Onsite Round (\d+)(?![\w])/g, (_, n: string) => LABEL_TEMPLATES.finalLoopRound({ n: Number(n) }));

/** Does this decline ask for the acknowledgement? The server's window, or a refusal that names the field. */
export const asksAcknowledgement = (withinWindow: boolean | undefined, error: unknown): boolean =>
  !!withinWindow || (error instanceof AppError && "notified_manager" in error.fieldErrors);

export interface DeclineFormProps {
  roundLabel: string;
  interviewStart?: Instant | null | undefined;
  reason: string;
  onReason?: ((text: string) => void) | undefined;
  /** The acknowledgement is asked (inside the notice window). */
  within: boolean;
  noticeHours?: number | undefined;
  acknowledged: boolean;
  onAcknowledged?: ((checked: boolean) => void) | undefined;
  error?: ReactNode | undefined;
}

export function DeclineForm({ roundLabel, interviewStart, reason, onReason, within, noticeHours, acknowledged, onAcknowledged, error }: DeclineFormProps) {
  const ackId = `${useId()}-ack`;
  return (
    <div className="pchg-decline">
      <KeyValue
        cols={1}
        items={[
          [CHANGE_LABELS.round, hisRoundLabel(roundLabel)],
          ...(interviewStart ? [[ACK_LABELS.interview, formatDateTime(interviewStart)] as const] : []),
        ]}
      />
      <Field label={SLIDE_LABELS.declineReason} required>
        <TextArea rows={3} maxLength={1000} value={reason} onChange={(event) => onReason?.(event.target.value)}  />
      </Field>
      <p className="pchg-note">{ACK_LABELS.declineOpens}</p>
      {within ? (
        <>
          {noticeHours ? <p className="pchg-note">{notifyWithin(noticeHours)}</p> : null}
          <span className="pchg-ack">
            <CheckTick checked={acknowledged} onChange={onAcknowledged} labelledBy={ackId}  />
            <span id={ackId}>{ACKNOWLEDGEMENT}</span>
          </span>
        </>
      ) : null}
      {error ? <ErrorStrip message={error} live /> : null}
    </div>
  );
}

export interface DeclineDialogProps {
  open: boolean;
  roundLabel: string;
  interviewStart?: Instant | null | undefined;
  /** The server's reading of the notice window for this seat (deviation: optional; the refusal still shows it). */
  withinWindow?: boolean | undefined;
  noticeHours?: number | undefined;
  busy?: boolean | undefined;
  error?: unknown;
  onClose: () => void;
  onConfirm: (input: { reason: string; notified_manager: boolean }) => void;
}

export function DeclineDialog({ open, roundLabel, interviewStart, withinWindow, noticeHours, busy, error, onClose, onConfirm }: DeclineDialogProps) {
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => { if (open) { setReason(""); setAcknowledged(false); } }, [open]);
  const within = asksAcknowledgement(withinWindow, error);
  const trimmed = reason.trim();
  const blocked = trimmed.length < 3 || (within && !acknowledged) ? { reason: COMMON_LABELS.required } : undefined;
  return (
    <Dialog
      preset="declineThisInterview"
      open={open}
      onClose={onClose}
      closeLabel={DIALOG_LABELS.keepThisInterview}
      dismissLabel={DIALOG_LABELS.keepThisInterview}
      primary={{
        label: ACTION_LABELS.decline,
        blocked,
        busy,
        hs: "ack.decline-confirm",
        onClick: () => { if (!blocked) onConfirm({ reason: trimmed, notified_manager: within && acknowledged }); },
      }}
    >
      <DeclineForm
        roundLabel={roundLabel}
        interviewStart={interviewStart}
        reason={reason}
        onReason={setReason}
        within={within}
        noticeHours={noticeHours}
        acknowledged={acknowledged}
        onAcknowledged={setAcknowledged}
        error={error ? errorText(error) : undefined}
      />
    </Dialog>
  );
}
